// ============================================================
// ColoniePaymentModal.js
// First payment: pick package + add-ons
// Follow-up: just enter amount toward existing balance
// ============================================================

import React, { useState, useEffect } from 'react';
import { X, Printer, Loader, AlertCircle } from 'lucide-react';
import { P, PKG_LABEL, computeFees } from './ColoniePrintUtils';

const ColoniePaymentModal = ({ registration, onClose, onSave, apiCall, showError }) => {
  const isFirst = !registration.EnrollmentID;

  // First-payment fields
  const [pkg, setPkg]         = useState('Cotisation');
  const [hasTr, setHasTr]     = useState(false);
  const [trf, setTrf]         = useState('');
  const [lp, setLp]           = useState('');
  const [hasNerd, setHasNerd] = useState(false);
  const [nerdType, setNT]     = useState('incamp');

  // Shared
  const [amount, setAmount]   = useState('');
  const [method, setMethod]   = useState('Cash');
  const [ref, setRef]         = useState('');
  const [notes, setNotes]     = useState('');
  const [discount, setDiscount]   = useState('');
  const [discountNote, setDiscNote] = useState('');
  const [saving, setSaving]   = useState(false);

  const f       = isFirst ? computeFees({ packageType: pkg, transportFee: trf, latePickup: lp, hasNerd, nerdType }) : null;
  const discountAmt    = isFirst ? parseFloat(discount || 0) : 0;
  const effectiveTotal = f ? Math.max(0, f.total - discountAmt) : 0;
  const balance = isFirst ? 0 : parseFloat(registration.BalanceRemaining || 0);

  // Auto-fill amount
  useEffect(() => {
    if (isFirst && f) setAmount(effectiveTotal.toFixed(2));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pkg, trf, lp, hasNerd, nerdType]);

  useEffect(() => {
    if (!isFirst) setAmount(balance.toFixed(2));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    const paid = parseFloat(amount);
    if (!paid || paid <= 0) return showError('Enter a valid amount');
    if (isFirst && discountAmt > 0 && !discountNote) return showError('Select a discount source (School or Colonie)');
    if (isFirst && f && paid > effectiveTotal + 0.01) return showError(`Amount exceeds total of $${effectiveTotal.toFixed(2)}`);
    if (!isFirst && paid > balance + 0.01)      return showError(`Amount exceeds balance of $${balance.toFixed(2)}`);

    setSaving(true);
    try {
      const body = {
        registrationId: registration.RegistrationID,
        amount: paid,
        paymentMethod: method,
        referenceNumber: ref || null,
        notes: notes || null,
        ...(isFirst && {
          packageType: pkg,
          hasTransport: hasTr,
          transportFee: parseFloat(trf || 0),
          latePickup: lp || null,
          hasNerd,
          nerdType,
          discount: discountAmt,
          discountNotes: discountAmt > 0 ? (discountNote || null) : null,
        }),
      };
      const result = await apiCall('/colonie/payments', { method: 'POST', body: JSON.stringify(body) });
      onSave(result);
    } catch (err) { showError(err.message); }
    finally { setSaving(false); }
  };

  const partialWarn = isFirst  && f && parseFloat(amount) > 0 && parseFloat(amount) < effectiveTotal - 0.001;
  const followPart  = !isFirst && parseFloat(amount) > 0 && parseFloat(amount) < balance - 0.001;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className={`flex items-center justify-between p-5 rounded-t-2xl shrink-0 ${isFirst ? 'bg-gradient-to-r from-green-600 to-teal-600' : 'bg-gradient-to-r from-blue-600 to-indigo-600'}`}>
          <div>
            <h2 className="text-lg font-bold text-white">{isFirst ? 'First Payment — Choose Package' : 'Add Payment'}</h2>
            <p className="text-sm text-white/80 mt-0.5">
              {registration.ChildName}{!isFirst && ` · Balance: $${balance.toFixed(2)}`}
            </p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1.5"><X size={18}/></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">

          {/* ── FIRST PAYMENT: package + add-ons ── */}
          {isFirst && (
            <>
              {/* Package */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Package <span className="text-red-400">*</span></p>
                <div className="space-y-2">
                  {[
                    { v: 'Cotisation',   label: 'Cotisation',            sub: 'Full season, one-time',      price: `$${P.cotisation}` },
                    { v: 'Season3Days',  label: '3 Days/Week — Season',  sub: '$275 full season, one-time', price: `$${P.season3Days}` },
                    { v: 'Monthly3Days', label: 'Monthly', sub: '$275/month, renewable', price: `$${P.monthly3Days}/mo` },
                    { v: 'NerdOnly', label: 'Nerd — Standalone', sub: 'Outside camp, no camp package', price: `${P.nerdOnly}` },
                    { v: 'Trial', label: 'Trial', sub: 'No add-ons, no portal confirm — converts later', price: `${P.trial}` },
                  ].map(o => (
                    <button key={o.v} onClick={() => setPkg(o.v)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border-2 text-left transition-all ${pkg === o.v ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <div>
                        <p className={`font-semibold text-sm ${pkg === o.v ? 'text-green-800' : 'text-gray-700'}`}>{o.label}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{o.sub}</p>
                      </div>
                      <span className={`font-bold text-sm ${pkg === o.v ? 'text-green-700' : 'text-gray-500'}`}>{o.price}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Add-ons */}
              {pkg !== 'Trial' && (
              <div className="space-y-3">
                <p className="text-sm font-semibold text-gray-700">
                  Add-ons known now
                  <span className="text-gray-400 font-normal text-xs ml-1">— more can be added later via the ➕ button</span>
                </p>

                {/* Transport */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={hasTr} onChange={e => setHasTr(e.target.checked)} className="w-4 h-4 accent-green-600"/>
                  <span className="text-sm text-gray-700 font-medium">Transportation</span>
                  <span className="text-xs text-gray-400">(fee TBD)</span>
                </label>
                {hasTr && (
                  <input type="number" value={trf} onChange={e => setTrf(e.target.value)}
                    placeholder="Transport fee ($)"
                    className="w-full ml-6 border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-green-500"/>
                )}

                {/* Late pickup */}
                <div>
                  <p className="text-sm text-gray-700 font-medium mb-1.5">Late Pickup</p>
                  <div className="flex gap-2">
                    {[
                      { v: '',      label: 'None' },
                      { v: '3to330', label: `3:00–3:30  ($${P.latePickup3to330})` },
                      { v: '3to4',   label: `3:00–4:00  ($${P.latePickup3to4})` },
                    ].map(o => (
                      <button key={o.v} onClick={() => setLp(o.v)}
                        className={`flex-1 py-2 px-1 rounded-xl border-2 text-xs font-medium ${lp === o.v ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600'}`}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Nerd add-on — hidden when NerdOnly selected (already included) */}
                {pkg !== 'NerdOnly' && (
                <div>
                  <label className="flex items-center gap-2 cursor-pointer mb-1.5">
                    <input type="checkbox" checked={hasNerd} onChange={e => setHasNerd(e.target.checked)} className="w-4 h-4 accent-green-600"/>
                    <span className="text-sm text-gray-700 font-medium">Nerd Package</span>
                  </label>
                  {hasNerd && (
                    <div className="flex gap-2 ml-6">
                      {[
                        { v: 'incamp',  label: `In Camp ($${P.nerdInCamp})` },
                        { v: 'outcamp', label: `Outside Camp ($${P.nerdOutCamp})` },
                      ].map(o => (
                        <button key={o.v} onClick={() => setNT(o.v)}
                          className={`flex-1 py-2 rounded-xl border-2 text-xs font-medium ${nerdType === o.v ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600'}`}>
                          {o.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                )}
              </div>

              )}
              {/* Total preview */}
              {f && (
                <div className="bg-gray-50 rounded-xl border p-3 space-y-1 text-sm">
                  {f.pkg > 0 && <div className="flex justify-between text-gray-600"><span>{PKG_LABEL[pkg]}</span><span>${f.pkg.toFixed(2)}</span></div>}
                  {f.tf  > 0 && <div className="flex justify-between text-gray-600"><span>Transport</span><span>${f.tf.toFixed(2)}</span></div>}
                  {f.lp  > 0 && <div className="flex justify-between text-gray-600"><span>Late Pickup</span><span>${f.lp.toFixed(2)}</span></div>}
                  {f.nf  > 0 && <div className="flex justify-between text-gray-600"><span>Nerd Package</span><span>${f.nf.toFixed(2)}</span></div>}
                  {discountAmt > 0 && (
                    <div className="flex justify-between text-emerald-600 font-medium border-t border-dashed border-gray-200 pt-1">
                      <span>Discount ({discountNote || '—'})</span>
                      <span>-${discountAmt.toFixed(2)}</span>
                    </div>
                  )}
                  <div className={`flex justify-between font-bold text-base pt-2 border-t border-gray-200 ${discountAmt > 0 ? 'text-emerald-700' : 'text-gray-800'}`}>
                    <span>Total{discountAmt > 0 ? ' after discount' : ''}</span>
                    <span>${effectiveTotal.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Discount — first payment only, not available for Trial */}
              {pkg !== 'Trial' && (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-700">
                    Discount <span className="text-gray-400 font-normal text-xs">(optional — first payment only)</span>
                  </p>
                  <div className="flex gap-2">
                    <input type="number" value={discount} onChange={e => setDiscount(e.target.value)} step="0.01"
                      placeholder="Amount ($)"
                      className="flex-1 border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500"/>
                    <select value={discountNote} onChange={e => setDiscNote(e.target.value)}
                      disabled={!parseFloat(discount || 0)}
                      className="flex-1 border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 disabled:opacity-40">
                      <option value="">Source</option>
                      <option value="School">School</option>
                      <option value="Colonie">Colonie</option>
                    </select>
                  </div>
                  {parseFloat(discount || 0) > 0 && !discountNote && (
                    <p className="text-xs text-red-500">Select School or Colonie</p>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── FOLLOW-UP: show enrollment summary, no re-selection ── */}
          {!isFirst && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-2">
              <p className="text-xs font-semibold text-blue-700 uppercase">Enrollment summary</p>
              <div className="flex justify-between text-sm text-blue-800">
                <span>{PKG_LABEL[registration.PackageType] || registration.PackageType}</span>
                <span className="font-semibold">${parseFloat(registration.TotalCharged || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm text-green-700">
                <span>Already paid</span>
                <span>${parseFloat(registration.TotalPaid || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold text-red-700 pt-1 border-t border-blue-200">
                <span>Remaining balance</span><span>${balance.toFixed(2)}</span>
              </div>
              <p className="text-xs text-gray-400 pt-1">To add a new add-on (e.g. late pickup), close this and use the ➕ button.</p>
            </div>
          )}

          {/* Amount */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Amount Paid ($)
              <span className="text-gray-400 font-normal text-xs ml-1">— enter less for partial payment</span>
            </label>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)} step="0.01"
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-xl font-bold focus:ring-2 focus:ring-green-500"/>
            {(partialWarn || followPart) && (
              <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                <AlertCircle size={11}/>
                Partial — ${((isFirst ? f?.total : balance) - parseFloat(amount || 0)).toFixed(2)} will remain outstanding
              </p>
            )}
          </div>

          {/* Method */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Payment Method</label>
            <div className="grid grid-cols-4 gap-2">
              {['Cash', 'Check', 'OMT', 'Whish'].map(m => (
                <button key={m} onClick={() => setMethod(m)}
                  className={`py-2 rounded-xl text-sm font-semibold border-2 ${method === m ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600'}`}>
                  {m}
                </button>
              ))}
            </div>
          </div>

          {(method === 'Check' || method === 'OMT' || method === 'Whish') && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Reference / Check #</label>
              <input value={ref} onChange={e => setRef(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-green-500"/>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Notes (optional)</label>
            <input value={notes} onChange={e => setNotes(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-green-500"/>
          </div>
        </div>

        <div className="flex gap-3 p-5 border-t shrink-0">
          <button onClick={onClose} className="flex-1 py-3 border border-gray-300 rounded-xl font-semibold text-gray-700">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className={`flex-1 py-3 text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2 ${isFirst ? 'bg-gradient-to-r from-green-600 to-teal-600' : 'bg-gradient-to-r from-blue-600 to-indigo-600'}`}>
            {saving ? <Loader size={16} className="animate-spin"/> : <Printer size={16}/>}
            {saving ? 'Processing…' : 'Record & Print Receipt'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ColoniePaymentModal;
